/**
 * ANIMATED PROVIDER LOGO
 *
 * The AI provider's logo (Claude / Codex / Gemini) with a gentle "breathing"
 * animation while the agent is working — the Claude Code-style thinking cue.
 * Falls back to a static logo when inactive or when the user prefers
 * reduced motion.
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { PROVIDER_META, type AiProvider } from "@/types/ai-chat";

interface Props {
  provider: AiProvider;
  size?: number;
  active?: boolean;
  className?: string;
}

export function AnimatedProviderLogo({ provider, size = 16, active = true, className }: Props) {
  const meta = PROVIDER_META[provider];
  const reduceMotion = useReducedMotion();
  const animating = active && !reduceMotion;

  return (
    <motion.img
      src={meta.logo}
      alt={meta.label}
      width={size}
      height={size}
      className={cn("flex-shrink-0 rounded-sm object-contain", className)}
      animate={
        animating
          ? { scale: [1, 1.18, 1], opacity: [0.65, 1, 0.65] }
          : { scale: 1, opacity: 1 }
      }
      transition={
        animating
          ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.2 }
      }
    />
  );
}
